import { useCallback, useEffect, useRef } from 'react'

const APP_HANDOFF_TIMEOUT_MS = 1500

/**
 * Attempts to hand off to a native app via a custom-scheme URL (e.g. `instagram://...`),
 * falling back to a web URL if nothing takes the tab into the background within the
 * timeout. A second call cancels any still-pending attempt so rapid re-clicks can't
 * stack timers/listeners or fire a stale fallback.
 *
 * The fallback tab is opened blank, synchronously, while still inside the click's user
 * gesture — `window.open` from a `setTimeout` callback gets popup-blocked, but navigating
 * a window we already hold a reference to does not. Because of that, this holds a
 * (non-`noopener`) reference to the tab until it's navigated or closed, so `fallbackUrl`
 * must be a trusted, non-user-controlled URL.
 */
export function useOpenAppLink() {
	const cancelPendingRef = useRef<(() => void) | null>(null)

	useEffect(() => () => cancelPendingRef.current?.(), [])

	return useCallback((appUrl: string, fallbackUrl: string, shouldFallback: () => boolean = () => true) => {
		cancelPendingRef.current?.()

		const fallbackWindow = window.open('', '_blank')
		let settled = false

		const settle = (openFallback: boolean) => {
			if (settled) return
			settled = true
			document.removeEventListener('visibilitychange', onVisibilityChange)
			window.clearTimeout(timeoutId)
			cancelPendingRef.current = null
			if (openFallback) {
				if (fallbackWindow) fallbackWindow.location.href = fallbackUrl
				else window.open(fallbackUrl, '_blank', 'noopener,noreferrer')
			} else {
				fallbackWindow?.close()
			}
		}

		const onVisibilityChange = () => {
			if (document.hidden) settle(false)
		}
		document.addEventListener('visibilitychange', onVisibilityChange)

		const timeoutId = window.setTimeout(() => settle(shouldFallback()), APP_HANDOFF_TIMEOUT_MS)

		cancelPendingRef.current = () => settle(false)

		window.location.href = appUrl
	}, [])
}
